import { z } from "zod";
import type { EqualWithDiff, Expect } from "@remi/ts-utils";

export const RoofMaterialTypeValues = [
  "ASPHALT",
  "ASPHALT_AND_NEW_TILE",
  "ASPHALT_AND_ORIGINAL_TILE",
  "CUSTOM",
  "DORMER_VENT",
  "DORMER_VENT_WITH_REROUTING",
  "EPDM",
  "FOAM_PENETRATION",
  "METAL",
  "NEEDS_MANUAL_ENTRY",
  "OTHER",
  "PURLIN",
  "RIDGE_CAP_REPLACEMENT",
  "SILICONE_COATING",
  "SISTERING",
  "THREE_TAB_SHINGLE",
  "TILE_REPLACE_WITH_NEW",
  "TPO",
  "VENT_REROUTING",
  "LICHEN_AND_MOSS",
  "SOLAR_THERMAL_REMOVAL",
  "FASCIA_BOARD",
  "TILE_RESET_ORIGINAL_CLAY",
  "TILE_RESET_ORIGINAL_CONCRETE",
  "SOLAR_PANELS",
  "PRESIDENTIAL_SHINGLE",
  "CLASS_4_SHINGLE",
  "GAF_HDZ",
  "GAF_HDZ_RS_COOL_COLORS",
  "IKO_DYNASTY",
  "IKO_CAMBRIDGE",
  "IKO_DYNASTY_COOL_COLORS",
  "IKO_CAMBRIDGE_COOL_COLORS",
  "UNKNOWN",
] as const;
export const RoofMaterialTypeSchema = z.enum(RoofMaterialTypeValues);
export type RoofMaterialType = (typeof RoofMaterialTypeValues)[number];
export const RoofMaterialType = RoofMaterialTypeSchema.enum;

export const AsphaltShingleMaterialValues = [
  RoofMaterialType.ASPHALT,
  RoofMaterialType.GAF_HDZ,
  RoofMaterialType.GAF_HDZ_RS_COOL_COLORS,
  RoofMaterialType.IKO_DYNASTY,
  RoofMaterialType.IKO_CAMBRIDGE,
  RoofMaterialType.IKO_DYNASTY_COOL_COLORS,
  RoofMaterialType.IKO_CAMBRIDGE_COOL_COLORS,
] as const satisfies RoofMaterialType[];
export const TileRoofMaterialValues = [
  RoofMaterialType.TILE_REPLACE_WITH_NEW,
  RoofMaterialType.TILE_RESET_ORIGINAL_CLAY,
  RoofMaterialType.TILE_RESET_ORIGINAL_CONCRETE,
  RoofMaterialType.ASPHALT_AND_NEW_TILE,
  RoofMaterialType.ASPHALT_AND_ORIGINAL_TILE,
] as const satisfies RoofMaterialType[];
export const LowSlopeRoofMaterialValues = [
  RoofMaterialType.TPO,
  RoofMaterialType.EPDM,
  RoofMaterialType.SILICONE_COATING,
] as const satisfies RoofMaterialType[];
const NamedShingleLineMaterialValues = [
  RoofMaterialType.GAF_HDZ,
  RoofMaterialType.GAF_HDZ_RS_COOL_COLORS,
  RoofMaterialType.IKO_DYNASTY,
  RoofMaterialType.IKO_CAMBRIDGE,
  RoofMaterialType.IKO_DYNASTY_COOL_COLORS,
  RoofMaterialType.IKO_CAMBRIDGE_COOL_COLORS,
] as const satisfies RoofMaterialType[];

// Premium shingle upgrades offered in the field-sales Best tier. Deliberately not named shingle lines or
// asphalt materials: they carry no color palette (color is suppressed like a custom shingle) and must not
// inherit the asphalt requirement line items (Rolled Comp, cut-in ridge vent).
export const UpgradeShingleMaterialValues = [
  RoofMaterialType.CLASS_4_SHINGLE,
  RoofMaterialType.PRESIDENTIAL_SHINGLE,
] as const satisfies RoofMaterialType[];
export type UpgradeShingleMaterial = (typeof UpgradeShingleMaterialValues)[number];

export const SelectableShingleLineMaterialValues = [
  ...NamedShingleLineMaterialValues,
  ...UpgradeShingleMaterialValues,
] as const satisfies RoofMaterialType[];
export const SelectableShingleLineMaterialSchema = z.enum(SelectableShingleLineMaterialValues);
export type SelectableShingleLineMaterial = (typeof SelectableShingleLineMaterialValues)[number];

export const PrevRoofMaterialTypeValues = [
  "ASPHALT",
  "METAL",
  "TILE",
  "OTHER",
  "SHAKE",
  "EPDM",
  "TPO",
  "ROLLED_COMP",
  "UNKNOWN",
] as const;
export const PrevRoofMaterialTypeSchema = z.enum(PrevRoofMaterialTypeValues);
export type PrevRoofMaterialType = (typeof PrevRoofMaterialTypeValues)[number];
export const PrevRoofMaterialType = PrevRoofMaterialTypeSchema.enum;

export const RoofMaterialTypeDisplay: Record<RoofMaterialType, string> = {
  ASPHALT: "Comp Shingle",
  ASPHALT_AND_NEW_TILE: "Comp-out w/ new tile",
  ASPHALT_AND_ORIGINAL_TILE: "Comp-out",
  CUSTOM: "Custom",
  DORMER_VENT: "Dormer Vent",
  DORMER_VENT_WITH_REROUTING: "Dormer Vent w/ rerouting",
  EPDM: "Epdm",
  FOAM_PENETRATION: "Foam Penetration",
  METAL: "Metal",
  NEEDS_MANUAL_ENTRY: "Needs Manual Entry",
  OTHER: "Other",
  PURLIN: "Purlin",
  RIDGE_CAP_REPLACEMENT: "Ridge Cap Replacement",
  SILICONE_COATING: "Silicone Coating",
  SISTERING: "Sistering",
  THREE_TAB_SHINGLE: "Three Tab Shingle",
  TILE_REPLACE_WITH_NEW: "New Tile",
  TPO: "Tpo",
  VENT_REROUTING: "Vent Rerouting",
  LICHEN_AND_MOSS: "Lichen and Moss",
  SOLAR_THERMAL_REMOVAL: "Solar Thermal Removal",
  FASCIA_BOARD: "Fascia Board",
  TILE_RESET_ORIGINAL_CLAY: "Lift and Relay Clay",
  TILE_RESET_ORIGINAL_CONCRETE: "Lift and Relay Concrete",
  SOLAR_PANELS: "Solar Panels",
  PRESIDENTIAL_SHINGLE: "Presidential Shingle",
  CLASS_4_SHINGLE: "Class 4 Shingle",
  GAF_HDZ: "GAF HDZ",
  GAF_HDZ_RS_COOL_COLORS: "GAF HDZ RS (Cool Colors)",
  IKO_DYNASTY: "IKO Dynasty",
  IKO_CAMBRIDGE: "IKO Cambridge",
  IKO_DYNASTY_COOL_COLORS: "IKO Dynasty Cool Colors",
  IKO_CAMBRIDGE_COOL_COLORS: "IKO Cambridge Cool Colors",
  UNKNOWN: "Unknown",
};

export const HomeownerRoofMaterialTypeDisplay: Record<RoofMaterialType, string> = {
  ...RoofMaterialTypeDisplay,
  CLASS_4_SHINGLE: "Impact-Resistant Shingle (Class 4)",
  ASPHALT: "Asphalt Shingle",
  ASPHALT_AND_ORIGINAL_TILE: "Tile with Asphalt Shingle Underlay",
  ASPHALT_AND_NEW_TILE: "New Tile + Asphalt Shingle",
  CUSTOM: "Custom",
  EPDM: "Rubber Roofing (EPDM)",
  TILE_RESET_ORIGINAL_CLAY: "Reuse Existing Clay Tiles",
  TILE_RESET_ORIGINAL_CONCRETE: "Reuse Existing Concrete Tiles",
  TILE_REPLACE_WITH_NEW: "New Tile",
  NEEDS_MANUAL_ENTRY: "Needs Manual Entry",
  PRESIDENTIAL_SHINGLE: "Luxury Architectural Shingle",
  SILICONE_COATING: "Protective Silicone Roof Coating",
  THREE_TAB_SHINGLE: "Basic 3-Tab Asphalt Shingle",
  TPO: "Single-Ply White Membrane (TPO)",
};

export const PrevRoofMaterialTypeDisplay: Record<PrevRoofMaterialType, string> = {
  ASPHALT: "Comp shingle",
  ROLLED_COMP: "Rolled comp",
  METAL: "Metal",
  OTHER: "Other",
  TILE: "Tile",
  TPO: "Tpo",
  EPDM: "Epdm",
  SHAKE: "Shake",
  UNKNOWN: "Unknown",
};

export const HomeownerPrevRoofMaterialTypeDisplay: Record<PrevRoofMaterialType, string> = {
  ASPHALT: "Asphalt Shingle",
  EPDM: "Rubber Roofing (EPDM)",
  ROLLED_COMP: "Rolled Roofing - Modified Bitumen",
  METAL: "Metal",
  OTHER: "Other",
  TILE: "Tile",
  TPO: "Single-Ply White Membrane (TPO)",
  SHAKE: "Cedar shake",
  UNKNOWN: "Unknown",
};

export const FloridaDisallowedRoofMaterials: RoofMaterialType[] = [
  RoofMaterialType.TILE_RESET_ORIGINAL_CLAY,
  RoofMaterialType.TILE_RESET_ORIGINAL_CONCRETE,
  RoofMaterialType.ASPHALT_AND_ORIGINAL_TILE,
];

export const UnsupportedRoofMaterialValues = [
  "SOLAR_PANELS",
  "SOLAR_THERMAL_REMOVAL",
  "LICHEN_AND_MOSS",
  "VENT_REROUTING",
  "DORMER_VENT",
  "DORMER_VENT_WITH_REROUTING",
  "FOAM_PENETRATION",
  "FASCIA_BOARD",
  "PURLIN",
  "RIDGE_CAP_REPLACEMENT",
  "SISTERING",
  "METAL",
] as const;
type UnsupportedRoofMaterial = (typeof UnsupportedRoofMaterialValues)[number];

const SupportedRoofMaterialValues = [
  "ASPHALT",
  "ASPHALT_AND_NEW_TILE",
  "ASPHALT_AND_ORIGINAL_TILE",
  "CUSTOM",
  "EPDM",
  "NEEDS_MANUAL_ENTRY",
  "OTHER",
  "SILICONE_COATING",
  "THREE_TAB_SHINGLE",
  "TILE_REPLACE_WITH_NEW",
  "TPO",
  "TILE_RESET_ORIGINAL_CLAY",
  "TILE_RESET_ORIGINAL_CONCRETE",
  "PRESIDENTIAL_SHINGLE",
  "CLASS_4_SHINGLE",
  "GAF_HDZ",
  "GAF_HDZ_RS_COOL_COLORS",
  "IKO_DYNASTY",
  "IKO_CAMBRIDGE",
  "IKO_DYNASTY_COOL_COLORS",
  "IKO_CAMBRIDGE_COOL_COLORS",
  "UNKNOWN",
] as const satisfies Exclude<RoofMaterialType, UnsupportedRoofMaterial>[];
const SupportedRoofMaterialSchema = z.enum(SupportedRoofMaterialValues);
export type SupportedRoofMaterial = (typeof SupportedRoofMaterialValues)[number];
export const SupportedRoofMaterial = SupportedRoofMaterialSchema.enum;

type _SupportedRoofMaterialTest = Expect<
  EqualWithDiff<RoofMaterialType, UnsupportedRoofMaterial | SupportedRoofMaterial>
>;
